import { apiService } from '../../../components/api/apiService';
import { logGovernedMcp } from './governedMcpLogger';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/reporting/ai-governance/dashboards';

export interface StatisticsDailyPoint {
  date: string;
  tokens: number;
  promptTokens?: number;
  completionTokens?: number;
  requestCount?: number;
}

export interface StatisticsHourlyPoint {
  hour: string;
  tokens: number;
  promptTokens?: number;
  completionTokens?: number;
  requestCount?: number;
}

export interface StatisticsModelPoint {
  model: string;
  tokens: number;
  promptTokens?: number;
  completionTokens?: number;
  requestCount?: number;
}

export interface StatisticsUserRow {
  userId: string;
  clientId: string;
  resourceId: string;
  resourceType: string;
  tokensUsed: number;
  promptTokens?: number;
  completionTokens?: number;
  requestCount?: number;
  tokenLimit: number;
  dailyUsage: StatisticsDailyPoint[];
  hourlyUsage?: StatisticsHourlyPoint[];
  byModel: StatisticsModelPoint[];
  updatedAt: string;
}

export interface StatisticsClientRow {
  id: string;
  name: string;
}

export type StatisticsMetric = 'tokens' | 'calls';

export interface StatisticsSeriesQuery {
  seriesFrom?: string;
  seriesTo?: string;
  hourlyDate?: string;
}

export interface AiGovernanceStatistics {
  month: string;
  resourceType: string;
  metric: StatisticsMetric;
  projectionSource: string;
  projectionAsOf: string | null;
  projectionReady: boolean;
  rebuildQueued: number;
  users: StatisticsUserRow[];
  clients: StatisticsClientRow[];
  resources: StatisticsClientRow[];
}

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'AI governance dashboard request failed'
  );
}

export const aiGovernanceDashboardApi = {
  async getStatistics(
    month: string,
    resourceType = 'client',
    series?: StatisticsSeriesQuery
  ): Promise<AiGovernanceStatistics> {
    const params: Record<string, string> = { month, resourceType };
    if (series?.seriesFrom) params.seriesFrom = series.seriesFrom;
    if (series?.seriesTo) params.seriesTo = series.seriesTo;
    if (series?.hourlyDate) params.hourlyDate = series.hourlyDate;
    logGovernedMcp('app', 'ai_governance_statistics_load', params);
    try {
      const data = (await apiService.get(`${BASE}/statistics`, params, CONNECTION)) as AiGovernanceStatistics;
      logGovernedMcp('app', 'ai_governance_statistics_load_ok', {
        month: data?.month,
        resourceType: data?.resourceType,
        metric: data?.metric,
        users: data?.users?.length,
        resources: data?.resources?.length,
        seriesFrom: series?.seriesFrom,
        seriesTo: series?.seriesTo,
        hourlyDate: series?.hourlyDate,
        hourlyPoints: (data?.users || []).reduce((sum, row) => sum + (row.hourlyUsage || []).length, 0),
        promptTokens: (data?.users || []).reduce((sum, row) => sum + (row.promptTokens || 0), 0),
        completionTokens: (data?.users || []).reduce((sum, row) => sum + (row.completionTokens || 0), 0),
        requestCount: (data?.users || []).reduce((sum, row) => sum + (row.requestCount || 0), 0),
        projectionReady: data?.projectionReady,
      });
      return data;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'ai_governance_statistics_load_failed', { month, resourceType, message, ...params });
      throw new Error(message);
    }
  },
};
